import type {
  APIRequestBody,
  APIRequestQueryParams,
  APIResponse,
} from './base-fetch';
import { get, post, put, remove } from './base-fetch';
import { buildFormData } from '@/helpers/build-form-data';

export enum ContentItemCategory {
  businessOpportunity = 'businessOpportunity',
  materials = 'materials',
  trends = 'trends',
}

type FilePayload = { file?: File };

export type ContentItemsQueryParams = Omit<
  APIRequestQueryParams<'/api/v4/content-items'>,
  'category'
> & {
  category?: ContentItemCategory;
};

type ContentItemBase = APIResponse<'/api/v4/content-items'>[number];

export type ContentItem = Omit<ContentItemBase, 'category'> & {
  category: ContentItemCategory;
  project?: { id: string; name: string } | null;
  projectId?: string | null;
};

export const fetchContentItems = (params?: ContentItemsQueryParams) =>
  get<ContentItem[]>('/v4/content-items', { params });

export const fetchFeedContentItems = (params?: ContentItemsQueryParams) =>
  get<ContentItem[]>('/v4/content-items/feed', { params });

export type CreateContentItemPayload = Omit<
  APIRequestBody<'/api/v4/content-items/create'>,
  'category'
> &
  FilePayload & {
    category: ContentItemCategory;
    projectId?: string;
  };

export const createContentItem = (payload: CreateContentItemPayload) => {
  const formData = buildFormData(payload);

  return post<ContentItem>('/v4/content-items/create', formData);
};

type UpdateContentItemBodyPayload = Omit<
  APIRequestBody<'/api/v4/content-items/{contentItemId}/update'>,
  'category'
> &
  FilePayload & {
    category: ContentItemCategory;
    projectId?: string;
  };

export type UpdateContentItemPayload = UpdateContentItemBodyPayload & {
  contentItemId: string;
};

export const updateContentItem = (payload: UpdateContentItemPayload) => {
  const { contentItemId, ...bodyPayload } = payload;
  const formData = buildFormData(bodyPayload);

  return put<ContentItem, FormData>(
    `/v4/content-items/${contentItemId}/update`,
    formData,
  );
};

export const toggleContentItemVisibility = (contentItemId: string) => {
  return put(`/v4/content-items/${contentItemId}/toggle-is-visible`);
};

export const deleteContentItem = (contentItemId: string) =>
  remove<void>(`/v4/content-items/${contentItemId}/delete`);

export type LearnMoreResponse =
  APIResponse<'/api/v4/content-items/{contentItemId}/learn-more'>;

export const sendLearnMoreInquiry = (contentItemId: string) =>
  post<LearnMoreResponse>(`/v4/content-items/${contentItemId}/learn-more`);

export type ContentItemProjectAccessResponse = {
  hasAccess: boolean;
  projectId: string | null;
};

export const fetchContentItemProjectAccess = (contentItemId: string) =>
  get<ContentItemProjectAccessResponse>(
    `/v4/content-items/${contentItemId}/project-access`,
  );
